/**
 * sanitizeHtml.ts — a tiny client-side allowlist strip for callback-note HTML.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the callback note editor is a light rich-text box (Bold/Italic/bullet
 * and numbered lists via `document.execCommand` — deprecated but
 * dependency-free) that posts its `innerHTML`. The SERVER sanitises every note
 * body before it is ever stored or re-served. This helper is DEFENCE IN DEPTH
 * only: before a note's `body` is ever handed to `dangerouslySetInnerHTML`,
 * strip it down to a small formatting-only tag allowlist — no attributes, no
 * scripts, no event handlers, no `style`/`src` survive. The ONE attribute
 * exception mirrors the server allowlist (clinic-service note-sanitizer.ts):
 * `href` on `<a>` is kept only when it starts `http://`/`https://` (a
 * `javascript:`/`data:`/relative href is dropped, leaving a bare `<a>` whose
 * text survives), and — since this IS the render step — the kept link gains
 * `target="_blank" rel="noopener noreferrer"` so it can never hijack the
 * console tab. Unknown/unsafe tags are UNWRAPPED (their safe text/children
 * survive) rather than dropped whole, so a stray wrapper never silently eats
 * real note content.
 */

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'BR', 'P', 'DIV', 'SPAN', 'A']);

/** Recursively copy only allow-listed elements (with no attributes) + text nodes. */
function appendSafeChildren(source: Node, target: Node): void {
  source.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      target.appendChild(document.createTextNode(node.textContent ?? ''));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      // Comments, processing instructions, etc. — dropped entirely.
      return;
    }
    const el = node as Element;
    // Normalise the tag name to UPPER before every comparison. WHY: an HTML
    // element's `tagName` is already upper-cased, but a FOREIGN-namespace one
    // (e.g. `<svg><script>…`) keeps its author-cased `tagName` — 'script',
    // lowercase — so a raw `=== 'SCRIPT'` check misses it and the element is
    // treated as unknown, UNWRAPPED, and its script text leaks through as plain
    // text. Upper-casing first closes that hole (and makes ALLOWED_TAGS robust
    // to any foreign-cased tag too).
    const tag = el.tagName.toUpperCase();
    if (tag === 'SCRIPT' || tag === 'STYLE') {
      // Never keep the content of a script/style tag, even as bare text.
      return;
    }
    if (ALLOWED_TAGS.has(tag)) {
      const clone = document.createElement(tag.toLowerCase());
      if (tag === 'A') {
        // Mirror the SERVER allowlist: http(s) hrefs only — anything else
        // (javascript:, data:, relative) leaves a bare <a> with its text.
        const href = el.getAttribute('href')?.trim() ?? '';
        if (/^https?:\/\//i.test(href)) {
          clone.setAttribute('href', href);
          clone.setAttribute('target', '_blank');
          clone.setAttribute('rel', 'noopener noreferrer');
        }
      }
      appendSafeChildren(el, clone);
      target.appendChild(clone);
    } else {
      // Unwrap: drop the tag itself, but keep walking its (safe) children.
      appendSafeChildren(el, target);
    }
  });
}

/**
 * Strip `html` down to the formatting-only allowlist above. Requires a DOM
 * (browser or jsdom) — this is a client-rendering concern, not a Node/SSR one.
 */
export function sanitizeNoteHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const container = document.createElement('div');
  appendSafeChildren(parsed.body, container);
  return container.innerHTML;
}
